import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import WarningIcon from '@mui/icons-material/Warning';
import { v4 as uuidv4 } from 'uuid';
import ProgrammesPicker from './programmes_picker';
import PlanNavigator from './plan_navigator';
import ProgrammesActionsPopupMenu from './programmes_actions_popup_menu';
import RecommenderWizardContainer from '../common/recommender_wizard_container';
import {
  PROGRAMME_PLANS_TITLE_PREFIX,
  emptyProgrammePlan,
  programmePlanPaginationIDs,
  programmeWithPlanAdded,
  programmesComparer,
} from '../../services/programmes';
import { ActivityObjectType } from '../../API';
import { userLocale } from '../../services/users';
import {
  useCreateContentEntryMutation,
  useCreateProgrammeMutation,
  useCreateSharedContentEntryMutation,
  useCreateSharedProgrammeMutation,
  useDeleteContentEntryMutation,
  useDeleteNoteMutation,
  useDeleteProgrammeMutation,
  useDeleteSharedProgrammeMutation,
  useGetContentEntriesByParentQuery,
  useGetProgrammesQuery,
  useGetSharedProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetFullMealsByParentsQuery,
  useUpdateMealMutation,
  useUpdateProgrammeMutation,
  useUpdateSharedProgrammeMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { otherUserActiveOnResourceSelector } from '../../features/server_status/server_status_slice';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  duplicateProgramme,
  programmePlanRecommendMeals,
  publishLatestVersionOfProgramme,
  removeMealsAndGRCRecipesFromProgrammePlan,
  removePlan,
  removeProgramme,
  unpublishProgramme,
} from '../../operations/programmes_operations';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import {
  currentUserSelector,
  userFriendlyNameForActivitySignalSelector,
} from '../../features/user_id/user_id_slice';
import PublishConfirmationNotificationModal from '../creator_studio/publish_confirmation_notification_modal';
import { sendNotificationToAllDevicesOperation } from '../../operations/push_notification_operations';

const ProgrammesNavigationBar = ({ selectedProgrammeId, selectedPlanId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery(undefined);

  const ownedProgrammes = useMemo(() => {
    if (!ownedProgrammesUnsorted) {
      return [];
    }
    const ownedProgrammesSorted = [...ownedProgrammesUnsorted];
    ownedProgrammesSorted.sort(programmesComparer);
    return ownedProgrammesSorted;
  }, [ownedProgrammesUnsorted]);

  const programme = (ownedProgrammes || []).find(
    (p) => p.id === selectedProgrammeId,
  );

  const { prevPlanId, nextPlanId } = programmePlanPaginationIDs(
    programme,
    selectedPlanId,
  );

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: healthProSpace } = useGetSpaceQuery();

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );

  const isProgrammePublished = (sharedProgrammes || []).some(
    (sp) => sp.programmeID === programme.id,
  );

  const { data: contentEntries } = useGetContentEntriesByParentQuery(
    programme?.id || '',
    {
      skip: !programme?.id,
    },
  );

  const [
    publishConfirmationNotificationVisible,
    setPublishConfirmationNotificationVisible,
  ] = useState(false);

  const [createProgramme] = useCreateProgrammeMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [deleteProgramme] = useDeleteProgrammeMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [lazyGetFullMealsByParents] = useLazyGetFullMealsByParentsQuery();

  const [updateSpace] = useUpdateSpaceMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [createContentEntry] = useCreateContentEntryMutation();

  const [createSharedContentEntry] = useCreateSharedContentEntryMutation();

  const [createSharedProgramme] = useCreateSharedProgrammeMutation();

  const [updateSharedProgramme] = useUpdateSharedProgrammeMutation();

  const [deleteSharedProgramme] = useDeleteSharedProgrammeMutation();

  const locale = userLocale(userProfile);

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const currentPLan = programme.plans.find((pl) => pl.id === selectedPlanId);
  const currentPlanIndex = programme.plans.findIndex(
    (pl) => pl.id === selectedPlanId,
  );
  const nextPlanIdAfterRemoval = nextPlanId || prevPlanId;

  const onCreateProgramme = () => {
    navigate(`/programmes/create`);
  };

  const onCreateNewWeek = async () => {
    const programmeId = selectedProgrammeId;
    const addAfterProgrammePlanId = selectedPlanId;
    const programmePlan = {
      title: `${PROGRAMME_PLANS_TITLE_PREFIX} ${currentPlanIndex + 2}`,
      id: uuidv4(),
      shortDescription: '',
      description: null,
      coverImageUrl: null,
      days: emptyProgrammePlan(),
      nutritionConstraints: null,
      showNutritionToUsers: null,
    };
    const updatedProgramme = programmeWithPlanAdded(
      programme,
      programmePlan,
      addAfterProgrammePlanId,
    );
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    await updateProgramme(updatedProgramme);
    const { prevPlanId: newPrevPlanId, nextPlanId: newNextPlanId } =
      programmePlanPaginationIDs(
        ownedProgrammes.find((pr) => pr.id === programmeId),
        addAfterProgrammePlanId,
      );
    const navigateToPlanId = newNextPlanId || newPrevPlanId;
    navigate(`/programmes/${selectedProgrammeId}/plans/${navigateToPlanId}`);
  };

  const onRemoveCurrentWeek = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    await removePlan(
      programme,
      selectedPlanId,
      deleteNote,
      deleteContentEntry,
      updateProgramme,
    );
    navigate(
      `/programmes/${selectedProgrammeId}/plans/${nextPlanIdAfterRemoval}`,
    );
  };

  const onEditProgramme = () => {
    navigate(`/programmes/${selectedProgrammeId}/edit`);
  };

  const onEditNutritionConstraints = () => {
    navigate(`/programmes/${selectedProgrammeId}/nutritionConstraints/edit`);
  };

  const onEditPlan = () => {
    navigate(`/programmes/${selectedProgrammeId}/plans/${selectedPlanId}/edit`);
  };

  /** TODO duplicate code with TopNavBarMobileProgrammes */
  const onDuplicateProgramme = async () => {
    const createdProgramme = await duplicateProgramme(
      programme,
      contentEntries,
      createProgramme,
      updateProgramme,
      createContentEntry,
    );
    navigate(`/programmes/${createdProgramme.id}`);
  };

  const onRemoveProgramme = async () => {
    // const mealIDs = (programme.recipesBoard?.menus || []).flatMap(
    //   (menu) => menu.mealIDs,
    // );
    // dispatch(mealsRemovedFromMealBasket(mealIDs));
    const firstRemainingProgramme = ownedProgrammes.find(
      (b) => b.id !== selectedProgrammeId,
    );
    await removeProgramme(
      programme,
      deleteNote,
      deleteContentEntry,
      deleteProgramme,
    );
    navigate(`/programmes/${firstRemainingProgramme.id}`);
  };

  const onRemoveMealsAndGRCRecipesFromPlan = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });

    return removeMealsAndGRCRecipesFromProgrammePlan(
      programme,
      selectedPlanId,
      updateProgramme,
    );
  };

  const onPublishAndNotify = async (
    shouldSendPushNotification,
    notificationTitle,
    notificationText,
  ) => {
    setPublishConfirmationNotificationVisible(false);
    await publishLatestVersionOfProgramme(
      programme,
      sharedProgrammes,
      contentEntries,
      healthProSpace,
      locale,
      createSharedContentEntry,
      createSharedProgramme,
      updateSharedProgramme,
      updateProgramme,
      updateSpace,
    );
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Your program has been published successfully',
      }),
    );
    if (shouldSendPushNotification) {
      console.log('Sending push notification');
      sendNotificationToAllDevicesOperation(
        notificationTitle,
        notificationText,
      );
    }
  };

  const onClickPublish = () => {
    setPublishConfirmationNotificationVisible(true);
  };

  const onUnpublish = async () => {
    await unpublishProgramme(
      programme.id,
      sharedProgrammes,
      healthProSpace,
      deleteSharedProgramme,
      updateSpace,
    );
    dispatch(
      showGlobalSnackbar({
        notificationText: 'Your program has been unpublished',
      }),
    );
  };

  const onPreview = () => {
    navigate(`/programmes/${programme.id}/plans/${selectedPlanId}/preview`);
  };

  const onChangePlan = (newPlanId) => {
    navigate(`/programmes/${programme.id}/plans/${newPlanId}`);
  };

  const [recommenderWizardVisible, setRecommenderWizardVisible] =
    useState(false);

  const onRecommendMeals = async (numDaysToPlan, startDayIndexOneBased) => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    programmePlanRecommendMeals(
      programme,
      selectedPlanId,
      numDaysToPlan,
      startDayIndexOneBased,
      userLocale(userProfile),
      lazyGetFullMealsByParents,
      updateMeal,
      updateProgramme,
      (notificationText) => dispatch(showGlobalSnackbar({ notificationText })),
    );
  };

  const onExport = () => {
    // dispatch(exportProgrammeAndObjectsAction(programme.id));
    // dispatch(exportPlannerBasedProgrammeAction('247-high-cholesterol'));
  };

  const importFileRef = useRef();

  const onImport = () => {
    if (importFileRef.current) {
      importFileRef.current.click();
    } else {
      console.warn('importFileRef.current is null or undefined');
    }
  };

  const onImportFileChosen = (/* fileList */) => {
    // const blob = fileList[0];
    // dispatch(
    //   importProgrammeAndObjectsAction(blob, (newProgramme) => {
    //     if (newProgramme) {
    //       navigate(`/programmes/${newProgramme.id}`);
    //     }
    //   }),
    // );
  };

  const { username: myID } = useSelector(currentUserSelector);

  const otherActiveUser = useSelector((state) =>
    otherUserActiveOnResourceSelector(
      state,
      myID,
      ActivityObjectType.PROGRAMMES,
      selectedProgrammeId,
    ),
  );

  const sharedProgrammePrevVersion = (sharedProgrammes || []).find(
    (sp) => sp.programmeID === programme.id,
  );

  const isPublished = !!sharedProgrammePrevVersion;

  return (
    <>
      <Box
        display="flex"
        className="plannerNavigationBar"
        justifyContent="center"
        style={{
          color: 'var(--app-primary-font-color)',
          position: 'relative',
          height: 40,
        }}>
        <div
          style={{
            padding: '0 10px',
            display: 'flex',
            position: 'absolute',
            left: 0,
            top: 0,
            height: 40,
          }}>
          <div style={{ margin: 'auto 0' }}>
            <ProgrammesPicker selectedProgrammeId={selectedProgrammeId} />
          </div>
        </div>
        <div style={{ margin: 'auto' }}>
          <PlanNavigator
            currentPlanId={selectedPlanId}
            plans={programme.plans}
            onChangePlan={onChangePlan}
            arrowStyle={{ color: 'var(--app-primary-font-color)' }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            right: 60,
            height: '40px',
            display: 'flex',
          }}>
          {isProgrammePublished && (
            <div
              style={{
                margin: 'auto 6px auto 0',
                padding: '4px',
                fontSize: '14px',
                backgroundColor: 'white',
                color: '#508f55',
              }}>
              Published
            </div>
          )}
          {otherActiveUser && (
            <>
              <Tooltip title="Modifying this program whilst another user is active may cause data loss">
                <WarningIcon
                  style={{
                    margin: 'auto 10px auto 0',
                    color: 'orange',
                  }}
                />
              </Tooltip>
              <div style={{ margin: 'auto 0', fontSize: '14px' }}>
                {otherActiveUser} is currently editing this program!
              </div>
            </>
          )}
        </div>
        <div
          style={{
            padding: '0 10px',
            position: 'absolute',
            right: 0,
            top: 0,
            height: 40,
          }}>
          <ProgrammesActionsPopupMenu
            isPublished={isPublished}
            fabStyle={{ color: 'var(--app-primary-font-color)' }}
            isRemoveCurrentWeekDisabled={programme.plans.length === 1}
            onCreateProgramme={onCreateProgramme}
            onImport={onImport}
            onCreateNewWeek={onCreateNewWeek}
            currentWeekTitle={currentPLan.title}
            onRemoveCurrentWeek={onRemoveCurrentWeek}
            onEditProgramme={onEditProgramme}
            onEditPlan={onEditPlan}
            onDuplicateProgramme={onDuplicateProgramme}
            onEditNutritionConstraints={onEditNutritionConstraints}
            onRecommendMeals={() => setRecommenderWizardVisible(true)}
            onRemoveMealsAndGRCRecipesFromPlan={
              onRemoveMealsAndGRCRecipesFromPlan
            }
            onPreview={onPreview}
            onPublish={onClickPublish}
            onUnpublish={onUnpublish}
            onExport={onExport}
            onRemoveProgramme={onRemoveProgramme}
          />
        </div>
      </Box>
      {recommenderWizardVisible && (
        <RecommenderWizardContainer
          onRecommendMeals={onRecommendMeals}
          onDismiss={() => setRecommenderWizardVisible(false)}
        />
      )}
      {publishConfirmationNotificationVisible && (
        <PublishConfirmationNotificationModal
          titleText="Publish your program"
          promptText="Are you sure you want to publish your program to your customers?"
          notificationTitleTemplate="New program available"
          notificationTextTemplate="Check out the latest program"
          onPublish={onPublishAndNotify}
          onDismiss={() => setPublishConfirmationNotificationVisible(false)}
        />
      )}
      <input
        ref={importFileRef}
        onChange={() => {
          onImportFileChosen(importFileRef.current.files);
          importFileRef.current.value = '';
        }}
        multiple={false}
        type="file"
        hidden
      />
    </>
  );
};

ProgrammesNavigationBar.propTypes = {
  selectedProgrammeId: PropTypes.string.isRequired,
  selectedPlanId: PropTypes.string.isRequired,
};

export default ProgrammesNavigationBar;
